import React, { useRef, useState } from "react";
import { Upload, Loader2, CheckCircle2, AlertCircle, Trash2 } from "lucide-react";
import { adminApi } from "../../../lib/api";

// Uploading the service-account key, for when a secrets field won't carry a
// multi-line file. The key is verified against the property before it is kept
// and is never readable back out, so this screen can only ever set or clear it.
export default function Ga4KeyUpload({ status, onChange }) {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState("");

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    setError("");
    setDone("");
    try {
      const body = new FormData();
      body.append("file", file);
      const res = await adminApi.ga4.uploadKey(body);
      setDone(res.service_account_email || "Connected");
      onChange?.();
    } catch (e) {
      setError(e?.response?.data?.detail || "The key could not be saved. Please try the file again.");
    } finally {
      setBusy(false);
      if (input.current) input.current.value = "";
    }
  };

  const remove = async () => {
    setBusy(true);
    setError("");
    try {
      await adminApi.ga4.deleteKey();
      setDone("");
      onChange?.();
    } catch {
      setError("The key could not be removed.");
    } finally {
      setBusy(false);
    }
  };

  const uploaded = status?.credentials_source === "uploaded";

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="ga4-key-upload">
      <h3 className="font-bold text-navy">Service account key</h3>
      <p className="text-sm text-slate-600 mt-1 leading-relaxed">
        Download the JSON key from Google Cloud → IAM &amp; Admin → Service Accounts → your account → Keys →
        Add key → Create new key → JSON, then upload that file here. It is checked against property{" "}
        {status?.property_id || "—"} before it is saved, stored encrypted, and never shown again.
      </p>

      {uploaded && (
        <div className="mt-4 flex flex-wrap items-center gap-3 rounded-lg bg-emerald-50 px-4 py-3" data-testid="ga4-key-present">
          <CheckCircle2 size={16} className="text-emerald-600 shrink-0" />
          <div className="min-w-0 text-xs">
            <p className="font-semibold text-navy [overflow-wrap:anywhere]">{status.service_account_email}</p>
            {status.key_uploaded_at && (
              <p className="text-slate-500 mt-0.5">
                Uploaded {new Date(status.key_uploaded_at).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={remove}
            disabled={busy}
            data-testid="ga4-key-remove"
            className="ml-auto inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-red-500 duration-200 [transition-property:color] disabled:opacity-50"
          >
            <Trash2 size={12} /> Remove
          </button>
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <input
          ref={input}
          type="file"
          accept="application/json,.json"
          onChange={(e) => upload(e.target.files?.[0])}
          className="hidden"
          data-testid="ga4-key-file-input"
        />
        <button
          type="button"
          onClick={() => input.current?.click()}
          disabled={busy}
          data-testid="ga4-key-upload-button"
          className="inline-flex items-center gap-2 rounded-full bg-navy px-5 py-2.5 text-xs font-bold text-white duration-200 [transition-property:background-color,opacity] hover:bg-navy/90 disabled:opacity-60"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
          {busy ? "Checking with Google…" : uploaded ? "Replace key file" : "Upload key file"}
        </button>
        <span className="text-[11px] text-slate-400">The file never leaves this admin — it is not sent anywhere else.</span>
      </div>

      {error && (
        <p className="mt-3 flex items-start gap-2 text-xs text-red-600" data-testid="ga4-key-error">
          <AlertCircle size={14} className="shrink-0 mt-px" /> <span>{error}</span>
        </p>
      )}
      {done && !error && (
        <p className="mt-3 flex items-start gap-2 text-xs text-emerald-600" data-testid="ga4-key-success">
          <CheckCircle2 size={14} className="shrink-0 mt-px" /> <span>Connected as {done}. Loading the figures…</span>
        </p>
      )}
    </div>
  );
}
